import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { usePendingJoinRequests, useApproveJoinRequest, useDeclineJoinRequest } from '../../hooks/useJoinRequests';
import { Spinner } from '../../components/common/Spinner';
import { Modal } from '../../components/common/Modal';
import { CurrencyRateInput } from '../../components/common/CurrencyRateInput';
import type { PendingJoinRequest } from '../../services/api/joinRequests';
import { getProjectRoles, type ProjectRoleOption } from '../../services/api/projects';
import { useAuth } from '../../services/authContext';
import { formatDisplayDate } from '../../utils/format';

function formatDate(dateString: string | null): string {
  return formatDisplayDate(dateString);
}

export function PendingJoinRequestsPage() {
  const { isAdmin } = useAuth();
  const [showAllProjects, setShowAllProjects] = useState(false);
  const { data: requests, isLoading, error } = usePendingJoinRequests(
    isAdmin && showAllProjects ? 'all' : undefined
  );
  const approveMutation = useApproveJoinRequest();
  const declineMutation = useDeclineJoinRequest();

  // Approve modal state
  const [approving, setApproving] = useState<PendingJoinRequest | null>(null);
  const [billingRate, setBillingRate] = useState('');
  const [hourlyCosts, setHourlyCosts] = useState('');
  const [userInvestment, setUserInvestment] = useState('0');
  const [roleName, setRoleName] = useState('');

  // F-10 (2026-05-13 UAT batch Phase 3) — role dropdown short-list for the
  // approving project. Backend returns ProjectCapacityNeed.roleId distinct,
  // falling back to the global ProjectRole catalog when the project has no
  // needs yet, so the PM is never stuck with an empty list. `enabled` keeps
  // the query dormant until the modal opens.
  const { data: projectRoles, isLoading: rolesLoading } = useQuery<ProjectRoleOption[]>({
    queryKey: ['project', approving?.projectId, 'roles'],
    queryFn: () => getProjectRoles(approving!.projectId),
    enabled: !!approving,
  });

  // Decline modal state
  const [declining, setDeclining] = useState<PendingJoinRequest | null>(null);
  const [declineReason, setDeclineReason] = useState('');

  const handleOpenApprove = (req: PendingJoinRequest) => {
    setApproving(req);
    setBillingRate('');
    setHourlyCosts(req.userHourlyCosts != null ? String(req.userHourlyCosts) : '');
    setUserInvestment('0');
    setRoleName('');
  };

  const handleApprove = async () => {
    if (!approving) return;
    // F-10 (2026-05-13) — role is now an explicit pick from a per-project
    // dropdown, no longer a free-text fallback to 'Developer'. Refuse to
    // submit without one rather than silently writing a role that may not
    // exist on this project.
    if (!roleName) return;
    try {
      await approveMutation.mutateAsync({
        id: approving.id,
        input: {
          // Phase 109 (D109-04): an empty field must persist NULL, because
          // an explicit 0 now means "deliberately non-billable" and is
          // allowed past the invoice generation guard.
          billingRate: billingRate ? parseFloat(billingRate) : null,
          hourlyCosts: hourlyCosts ? parseFloat(hourlyCosts) : null,
          userInvestment: userInvestment ? parseFloat(userInvestment) : null,
          roleName,
        },
      });
      setApproving(null);
    } catch {
      // Error shown in UI
    }
  };

  const handleOpenDecline = (req: PendingJoinRequest) => {
    setDeclining(req);
    setDeclineReason('');
  };

  const handleDecline = async () => {
    if (!declining) return;
    try {
      await declineMutation.mutateAsync({
        id: declining.id,
        input: { reason: declineReason || undefined },
      });
      setDeclining(null);
    } catch {
      // Error shown in UI
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg">
        Failed to load pending join requests.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Project Join Requests</h1>
        <div className="flex items-center gap-4">
          {isAdmin && (
            <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={showAllProjects}
                onChange={(e) => setShowAllProjects(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Show all projects
            </label>
          )}
          <span className="text-sm text-gray-500">
            {requests?.length || 0} pending request{requests?.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {requests?.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
          No pending join requests.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requested</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {requests?.map((req) => (
                <tr key={req.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{req.userName || req.userEmail}</div>
                    <div className="text-xs text-gray-500">{req.userEmail}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{req.projectName}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(req.requestedStartDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(req.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleOpenApprove(req)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <CheckIcon className="w-3.5 h-3.5" />
                        Approve
                      </button>
                      <button
                        onClick={() => handleOpenDecline(req)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                      >
                        <XMarkIcon className="w-3.5 h-3.5" />
                        Decline
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Approve Modal - Assignment Details */}
      <Modal
        isOpen={!!approving}
        onClose={() => setApproving(null)}
        title={`Approve: ${approving?.userName || ''} → ${approving?.projectName || ''}`}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Requested start date: <span className="font-medium">{formatDate(approving?.requestedStartDate ?? null)}</span>
          </p>

          <div className="grid grid-cols-2 gap-4">
            {/* F-09 (2026-05-13 UAT batch Phase 3) — Billing Rate now
                accepts foreign-currency input via CurrencyRateInput.
                Storage stays CZK; the component blanks `billingRate`
                until the user clicks Convert, and our handleApprove
                falls back to 0 on empty so the validation gate is
                obvious (no silent zero submission of a 2500 EUR rate). */}
            <div>
              <CurrencyRateInput
                label="Billing Rate"
                value={billingRate}
                onChange={setBillingRate}
                placeholder="e.g. 2500"
                inputId="jr-approve-billing-rate"
              />
            </div>
            <div>
              {/* Hourly Costs is the employee's internal loaded cost —
                  stored in CZK and entered in CZK by convention (mirrors
                  AssignmentForm.tsx:273 which keeps this input fixed
                  CZK). Only the customer-facing Billing Rate gets the
                  multi-currency dropdown. */}
              <label className="block text-sm font-medium text-gray-700 mb-1">Hourly Costs (CZK/h)</label>
              <input
                type="number"
                value={hourlyCosts}
                onChange={(e) => setHourlyCosts(e.target.value)}
                placeholder="e.g. 800"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              {/* F-11 (2026-05-13 UAT batch Phase 3) — label was "(%)" but
                  Investment Rate is a per-hour CZK amount used by
                  InvestmentSummarySection as `Total Investment = Hours ×
                  Investment Rate`, NOT a percentage. AssignmentForm.tsx:285
                  already uses "(Kč/hr)"; aligning here. */}
              <label
                className="block text-sm font-medium text-gray-700 mb-1"
                title="Per-hour CZK amount. Total investment = Hours × Investment Rate."
              >
                Investment Rate (CZK/h)
              </label>
              <input
                type="number"
                value={userInvestment}
                onChange={(e) => setUserInvestment(e.target.value)}
                placeholder="0"
                title="Per-hour CZK amount. Total investment = Hours × Investment Rate."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              {/* F-10 (2026-05-13 UAT batch Phase 3) — text input replaced
                  with a dropdown of roles fetched per-project from
                  GET /api/projects/:id/roles. Falls back to the global
                  ProjectRole catalog server-side when the project has no
                  capacity-need rows yet. */}
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                disabled={rolesLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100"
              >
                <option value="">
                  {rolesLoading ? 'Loading roles…' : 'Select a role…'}
                </option>
                {(projectRoles ?? []).map((r) => (
                  <option key={r.id} value={r.name}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {approveMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {(approveMutation.error as Error)?.message || 'Failed to approve request'}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => setApproving(null)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleApprove}
              disabled={approveMutation.isPending || !roleName}
              title={!roleName ? 'Pick a role before approving.' : undefined}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {approveMutation.isPending ? 'Approving...' : 'Approve & Create Assignment'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Decline Modal */}
      <Modal
        isOpen={!!declining}
        onClose={() => setDeclining(null)}
        title={`Decline: ${declining?.userName || ''} → ${declining?.projectName || ''}`}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
            <textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="e.g. Project is fully staffed at the moment"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {declineMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {(declineMutation.error as Error)?.message || 'Failed to decline request'}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => setDeclining(null)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDecline}
              disabled={declineMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {declineMutation.isPending ? 'Declining...' : 'Decline Request'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default PendingJoinRequestsPage;
